import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { Check, Copy } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { useMcpStore } from '@/frontend/store/McpStore';
import { Breadcrumbs } from '../Breadcrumbs';
import { TwoPanelPlaceholder } from '../TwoPanelPlaceholder';

function CopyableCode({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard access refused; the value is on screen to copy by hand.
    }
  };

  return (
    <div className="d-flex align-items-start gap-2 bg-light rounded p-2">
      <code className="flex-grow-1 py-1 text-break" style={{ whiteSpace: 'pre-wrap' }}>{value}</code>
      <Button variant="outline-secondary" size="sm" className="d-flex align-items-center gap-1 flex-shrink-0" onClick={() => void copy()}>
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {I18n.get(copied ? 'aiCopied' : 'aiCopyCommand')}
      </Button>
    </div>
  );
}

/** Setup instructions for connecting Claude Code to the MCP server. */
export function AiAssistantPage() {
  const connection = useMcpStore((state) => state.connection);
  const loadFailed = useMcpStore((state) => state.connectionLoadFailed);
  const fetchConnectionInfo = useMcpStore((state) => state.fetchConnectionInfo);

  useEffect(() => {
    void fetchConnectionInfo();
  }, [fetchConnectionInfo]);

  if (loadFailed) {
    return (
      <div className="d-flex flex-column">
        <Breadcrumbs items={[{ title: I18n.get('aiAssistant') }]} />
        <div className="p-4">
          <Alert variant="danger" className="mb-0">{I18n.get('aiAssistantLoadFailed')}</Alert>
        </div>
      </div>
    );
  }

  if (!connection) {
    return <TwoPanelPlaceholder />;
  }

  return (
    <div className="d-flex flex-column">
      <Breadcrumbs items={[{ title: I18n.get('aiAssistant') }]} />
      <div className="p-4 d-flex flex-column gap-4" style={{ maxWidth: '900px' }}>
        <div>
          <h1 className="card-title mb-1">{I18n.get('aiAssistant')}</h1>
          <p className="text-muted mb-0">{I18n.get('aiAssistantDescription')}</p>
        </div>

        {!connection.enabled && <Alert variant="warning" className="mb-0">{I18n.get('aiAssistantNotConfigured')}</Alert>}
        {connection.devAuthBypass && <Alert variant="info" className="mb-0">{I18n.get('aiAssistantDevBypass')}</Alert>}

        {/* Without a server URL there is nothing to connect to, and the command would be broken. */}
        {connection.enabled && (
          <>
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">{I18n.get('aiAssistantConnectTitle')}</h5>
                <ol className="mb-0 d-flex flex-column gap-3 ps-3">
                  <li>
                    <p className="mb-2">{I18n.get('aiAssistantConnectStep1')}</p>
                    <CopyableCode value={connection.claudeCodeCommand} />
                  </li>
                  <li>{I18n.get('aiAssistantConnectStep2', { serverName: connection.serverName })}</li>
                  <li>
                    {I18n.get('aiAssistantConnectStep3')}{' '}
                    <Link to="/ai-approvals">{I18n.get('aiApprovals')}</Link>
                  </li>
                </ol>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h5 className="card-title">{I18n.get('aiAssistantServerUrl')}</h5>
                <p className="text-muted">{I18n.get('aiAssistantOtherClients')}</p>
                <CopyableCode value={connection.serverUrl} />
              </div>
            </div>
          </>
        )}

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">{I18n.get('aiAssistantHowItWorksTitle')}</h5>
            <p className="mb-0">{I18n.get('aiAssistantHowItWorks')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
